import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import i18n from 'simple-react-i18n'
import { Accordion, AccordionDetails, AccordionSummary, ButtonGroup, Grid, Icon, Tooltip } from '@mui/material'
import ProgressCard from 'components/card/ProgressCard'
import { groupBy, isEmpty, keys, range, round, sumBy, toLower } from 'lodash'
import { getDate, getMonthYear, getWeekYear, getYear } from 'utils/DateUtil'
import Select from 'components/forms/Select'
import { formatMilliers, getI18nTitleDataLength, geti18n } from 'utils/StringUtil'
import { nbPerPageLabelShort } from 'referencial/constants/ReferencialConstants'
import { ButtonMUI } from 'components/styled/Buttons'
import ProductionUnitAction from 'productionUnit/actions/ProductionUnitAction'
import { exportFile } from 'utils/ExportDataUtil'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { getLocalStorageJson } from 'utils/FormUtils'
import { NewTable } from 'components/datatable/NewTable'
import { STATION_NAME, STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import InstallationAction from 'installation/actions/InstallationAction'
import moment from 'moment'
import DtoInstallationAnalysisMeasureLight from 'installation/dto/installation/DtoInstallationAnalysisMeasureLight'
import { DISABLED_LINK, PRODUCTION_UNIT_LINK_TYPES } from 'productionUnit/constants/ProductionUnitConstants'
import { lightGrey } from 'utils/constants/ColorTheme'
import { INSTALLATION_TYPES } from 'installation/constants/InstallationConstants'
import { getSiteType } from 'utils/mapUtils/SiteTypes'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import { HYDRO_TYPEID } from 'hydrometry/constants/HydrometryConstants'
import { promiseAllProgress } from 'utils/ActionUtils'

const OCCURENCE_TYPE = {
    DAY: { value: 'DAY', defaultValue: 7, nbValue: 30 },
    WEEK: { value: 'WEEK', defaultValue: 8, nbValue: 52 },
    MONTH: { value: 'MONTH', defaultValue: 12, nbValue: 24 },
    YEAR: { value: 'YEAR', defaultValue: 5, nbValue: 20 },
}

const INST_PREL_PARAMETER = '1098'

const ProductionUnitLinkedPrel = () => {
    const dispatch = useDispatch()
    const [piezoDataLoaded, setPiezoDataLoaded] = useState(false)
    const [hydroDataLoaded, setHydroDataLoaded] = useState(false)
    const [instDataLoaded, setInstDataLoaded] = useState(false)
    // occurrence transmise par le lien depuis la gestion des volumes
    const { occurenceFilter } = useLocation().state || {}
    const initialOccurence = OCCURENCE_TYPE[occurenceFilter?.occurence] || OCCURENCE_TYPE.DAY
    const [occurence, setOccurence] = useState(initialOccurence)
    const [nbOccurence, setNbOccurence] = useState(occurenceFilter?.nbOccurence || initialOccurence.defaultValue)

    const [linkedInstPrel, setLinkedInstPrel] = useState([])
    const [linkedHydroPrel, setLinkedHydroPrel] = useState([])

    const buttonsOccurence = [
        { value: 'DAY', title: i18n.day, borderWidth: '2px 0px 2px 2px' },
        { value: 'WEEK', title: i18n.week, borderWidth: 2 },
        { value: 'MONTH', title: i18n.month, borderWidth: '2px 1px' },
        { value: 'YEAR', title: i18n.year, borderWidth: '2px 2px 2px 1px' },
    ]

    const [dashboardDisplay, setDashboardDisplay] = useLocalStorage('DASHBOARD_DISPLAY', {})

    const formatDate = useCallback(date => {
        switch (occurence.value) {
            case 'WEEK':
                return getWeekYear(date)
            case 'MONTH':
                return getMonthYear(date)
            case 'YEAR':
                return getYear(date)
            default:
                return getDate(date)
        }
    }, [occurence])

    const {
        linkedPiezoPrel,
        prodUnitLinks,
        piezos,
        installations,
        hydros,
    } = useSelector(store => ({
        linkedPiezoPrel: store.ProductionUnitReducer.linkedPiezoPrel,
        prodUnitLinks: store.ProductionUnitReducer.prodUnitLinks,
        piezos: store.PiezometryReducer.piezosIndex,
        installations: store.InstallationReducer.installationsIndex,
        hydros: store.HydrometryReducer.hydrosIndex,
    }), shallowEqual)

    const [piezoIds, instIds, hydroIds] = useMemo(() => {
        return [
            prodUnitLinks.filter(as => as.siteType === STATION_TYPE_CONSTANT.piezometry && as.status !== DISABLED_LINK).map(s => s.siteCode),
            prodUnitLinks.filter(as => as.siteType === STATION_TYPE_CONSTANT.installation && parseInt(as.linkType) >= PRODUCTION_UNIT_LINK_TYPES.DISTRIBUTION && as.status !== DISABLED_LINK).map(s => s.siteCode),
            prodUnitLinks.filter(as => as.siteType === STATION_TYPE_CONSTANT.hydrometry && as.status !== DISABLED_LINK).map(s => s.siteCode),
        ]
    }, [prodUnitLinks])

    useEffect(() => {
        if (isEmpty(installations)) {
            dispatch(InstallationAction.fetchInstallationsLight())
        }
    }, [])

    useEffect(() => {
        setLinkedInstPrel([])
        setInstDataLoaded(false)
        if (instIds.length) {
            const promises = instIds.map(id => {
                return InstallationAction.promiseInstallationAnalysisByCriterias(id, {
                    lightMode: true,
                    startDate: moment().subtract(occurence.value, nbOccurence).valueOf(),
                    endDate: moment().valueOf(),
                    parameterCode: INST_PREL_PARAMETER,
                    group: 'MAX',
                }).then(json => {
                    const grouped = groupBy(json.map(d => new DtoInstallationAnalysisMeasureLight(d)), d => formatDate(d.date))
                    setLinkedInstPrel(prev => [
                        ...prev,
                        ...keys(grouped).map(key => {
                            const current = grouped[key]
                            return {
                                ...current[0],
                                measureDate: current[0].date,
                                value: sumBy(current, c => c.value),
                            }
                        }),
                    ])
                })
            })
            promiseAllProgress(promises).then(() => setInstDataLoaded(true))
        } else {
            setInstDataLoaded(true)
        }
    }, [instIds, occurence, nbOccurence, dispatch, formatDate])

    useEffect(() => {
        setLinkedHydroPrel([])
        setHydroDataLoaded(false)
        if (hydroIds.length) {
            const promises = hydroIds.map(id => {
                return HydrometryAction.promiseHydroChronicMeasuresLight({
                    chartMode: true,
                    stationId: id,
                    dataType: HYDRO_TYPEID.SAMPLE,
                    groupFunc: 'RAW',
                    startDate: moment().subtract(occurence.value, nbOccurence).valueOf(),
                    lightMode: true,
                }).then(json => {
                    const grouped = groupBy(json, d => formatDate(d.date))
                    setLinkedHydroPrel(prev => [
                        ...prev,
                        ...keys(grouped).map(key => {
                            const current = grouped[key]
                            return {
                                ...current[0],
                                measureDate: current[0].date,
                                value: sumBy(current, c => c.value),
                            }
                        }),
                    ])
                })
            })
            promiseAllProgress(promises).then(() => setHydroDataLoaded(true))
        } else {
            setHydroDataLoaded(true)
        }
    }, [hydroIds, occurence, nbOccurence, dispatch, formatDate])

    useEffect(() => {
        if (piezoIds.length && occurence && nbOccurence) {
            setPiezoDataLoaded(false)
            dispatch(ProductionUnitAction.fetchLinkedPiezoPrel({ ids: piezoIds, occurence: occurence.value, nbOccurence })).then(() => setPiezoDataLoaded(true))
        } else {
            setPiezoDataLoaded(true)
        }
    }, [piezoIds, occurence, nbOccurence, dispatch])

    const getStationName = useCallback(({ id, code, designation, name } = {}) => {
        const idCode = code ? `${code}${designation ? `/${designation}` : ''}` : id
        return `${idCode} ${name ? `\n${name}` : ''}`
    }, [])

    const { headers, exportHeaders } = useMemo(() => {
        const addedPiezos = piezoIds.map(idPiezo => {
            return {
                key: `${STATION_TYPE_CONSTANT.piezometry}_${idPiezo}`,
                value: (
                    <Tooltip title={<span key={piezos[idPiezo]?.code} style={{ whiteSpace: 'pre-wrap' }}>{getStationName(piezos[idPiezo])}</span>}>
                        <Grid container alignItems='center' spacing={1}>
                            <img style={{ width: '1rem' }} src={getSiteType(STATION_NAME.PIEZOMETER)?.url} />
                            {piezos[idPiezo]?.code || '' }
                        </Grid>
                    </Tooltip>
                ),
                exportValue: getStationName(piezos[idPiezo]),
            }
        })

        const addedInsts = instIds.map(idInst => {
            const inst = installations[idInst] || {}
            const instType = INSTALLATION_TYPES.find(it => it.code === inst?.installationType) || {}
            return {
                value: (
                    <Tooltip title={<span key={inst?.code} style={{ whiteSpace: 'pre-wrap' }}>{getStationName(inst)}</span>}>
                        <Grid container alignItems='center' spacing={1}>
                            <img style={{ width: '1rem' }} src={getSiteType(instType.img)?.url} />
                            {inst.code || inst.name || inst.designation || '' }
                        </Grid>
                    </Tooltip>
                ),
                key: `${STATION_TYPE_CONSTANT.installation}_${idInst}`,
                exportValue: getStationName(inst),
            }
        })

        const addedHydros = hydroIds.map(idHydro => {
            return {
                key: `${STATION_TYPE_CONSTANT.hydrometry}_${idHydro}`,
                value: (
                    <Tooltip title={<span key={hydros[idHydro]?.code} style={{ whiteSpace: 'pre-wrap' }}>{getStationName(hydros[idHydro])}</span>}>
                        <Grid container alignItems='center' spacing={1}>
                            <img style={{ width: '1rem' }} src={getSiteType(STATION_NAME.HYDROMETRIC_STATION)?.url} />
                            {hydros[idHydro]?.code || '' }
                        </Grid>
                    </Tooltip>
                ),
                exportValue: getStationName(hydros[idHydro]),
            }
        })

        const tableHeaders = ['date', ...addedPiezos, ...addedHydros, 'entrySum', ...(!isEmpty(addedInsts) ? [...addedInsts, 'outSum', 'difference'] : [])]

        return {
            headers: tableHeaders,
            exportHeaders: tableHeaders.map(th => th?.exportValue || th),
        }
    }, [getStationName, instIds, installations, piezoIds, piezos, hydroIds, hydros])


    const data = useMemo(() => {
        const lines = range(nbOccurence).map((_, i) => formatDate(moment().subtract(occurence.value, i)))
        return lines.map((date, index) => {
            const piezosData = linkedPiezoPrel.filter(d => formatDate(d.measureDate) === date).reduce((acc, l) => {
                return {
                    ...acc,
                    [`${STATION_TYPE_CONSTANT.piezometry}_${l.idPiezometer}`]: formatMilliers(round(l.value, 2)),
                    sum: acc.sum + l.value,
                    [getStationName(piezos[l.idPiezometer])]: formatMilliers(round(l.value, 2)),
                }
            }, { sum: 0 })
            const insts = linkedInstPrel.filter(d => formatDate(d.measureDate) === date).reduce((acc, l) => {
                return {
                    ...acc,
                    [`${STATION_TYPE_CONSTANT.installation}_${l.instId}`]: round(l.value, 2),
                    sum: acc.sum + l.value,
                    [getStationName(installations[l.instId])]: formatMilliers(round(l.value, 2)),
                }
            }, { sum: 0 })
            const hydroData = linkedHydroPrel.filter(d => formatDate(d.measureDate) === date).reduce((acc, l) => {
                return {
                    ...acc,
                    [`${STATION_TYPE_CONSTANT.hydrometry}_${l.hydroId}`]: round(l.value, 2),
                    sum: acc.sum + l.value,
                    [getStationName(hydros[l.hydroId])]: formatMilliers(round(l.value, 2)),
                }
            }, { sum: 0 })

            return {
                date,
                ...piezosData,
                ...hydroData,
                entrySum: { value: formatMilliers(round(piezosData.sum + hydroData.sum, 2)) || '', color: lightGrey },
                ...insts,
                outSum: { value: formatMilliers(round(insts.sum, 2)) || '', color: lightGrey },
                difference: { value: formatMilliers(round(piezosData.sum - insts.sum, 2)) || '', color: lightGrey },
                headers: index === 0 ? exportHeaders : [],
            }
        })
    }, [nbOccurence, formatDate, occurence.value, linkedPiezoPrel, linkedInstPrel, linkedHydroPrel, exportHeaders, getStationName, piezos, installations, hydros])

    const selectValues = useMemo(() => {
        const labelKey = toLower(occurence.value)
        return range(occurence.nbValue).map(index => ({ label: `${index + 1} ${getI18nTitleDataLength(geti18n(labelKey), geti18n(`${labelKey}s`), index + 1)}`, id: index + 1 }))
    }, [occurence])

    if (isEmpty(piezoIds) && isEmpty(instIds) && isEmpty(hydroIds)) {
        return null
    }

    const dataLoaded = piezoDataLoaded && instDataLoaded && hydroDataLoaded

    return (
        <Accordion
            expanded={dashboardDisplay?.PROD?.PREL}
            onChange={(_, v) => {
                const actualDashboardDisplay = getLocalStorageJson('DASHBOARD_DISPLAY')
                setDashboardDisplay({
                    ...actualDashboardDisplay,
                    PROD: {
                        ...actualDashboardDisplay?.PROD,
                        PREL: v,
                    },
                })
            }}
        >
            <AccordionSummary fontColor={'black'} color='white'>
                <Grid container justifyContent='space-between' alignItems='center' sx={{ width: '100%' }}>
                    <Grid>{i18n.production}</Grid>
                    <Grid>
                        <Icon
                            color='black'
                            onClick={() => {
                                exportFile({
                                    data,
                                    exportType: 'xlsx',
                                    titleFile: i18n.production,
                                })
                            }}
                        >file_download</Icon>
                    </Grid>
                </Grid>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid size={6}>
                        <ButtonGroup>
                            {
                                buttonsOccurence.map((o) => (
                                    <ButtonMUI
                                        key={o.value}
                                        variant={occurence.value === o.value ? 'contained' : 'outlined'}
                                        onClick={() => {
                                            setOccurence(OCCURENCE_TYPE[o.value])
                                            setNbOccurence(OCCURENCE_TYPE[o.value].defaultValue)
                                        }}
                                        style={{ border: 'solid rgba(53, 96, 159, 1)', borderWidth: o.borderWidth, fontWeight: 600 }}
                                    >{o.title}</ButtonMUI>
                                ))
                            }
                        </ButtonGroup>
                    </Grid>
                    <Grid size={6}>
                        <Select
                            value={nbOccurence}
                            label={i18n.occurency}
                            options={selectValues}
                            noSort
                            onChange={setNbOccurence}
                        />
                    </Grid>
                    <Grid size={12}>
                        {!dataLoaded && <ProgressCard indeterminate className='padding-left-2 padding-right-1 padding-top-1' />}
                        {dataLoaded && (
                            <NewTable
                                rows={data}
                                headers={headers}
                                rowsPerPageOptions={nbPerPageLabelShort}
                            />
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
}

ProductionUnitLinkedPrel.propTypes = {
}

export default ProductionUnitLinkedPrel